import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Plus, BookHeart, LogOut, ChefHat, Wheat, Star, Shuffle, Image, Settings, CalendarDays, Send, House } from "lucide-react";
import { api, useAuth } from "@/lib/auth";
import BirthdayBanner from "@/components/BirthdayBanner";
import PushNotificationsPrompt from "@/components/PushNotificationsPrompt";
import FeaturesGuide from "@/components/FeaturesGuide";
import SundayMode from "@/components/SundayMode";

function PhotoOrPlaceholder({ photo, alt }) {
  if (photo) return <img src={photo} alt={alt} loading="lazy" className="w-full h-44 object-cover" />;
  return (
    <div className="w-full h-44 flex items-center justify-center bg-gradient-to-br from-amber-light/30 to-paper-warm">
      <ChefHat className="w-12 h-12 text-amber-deep/50" />
    </div>
  );
}

function Chip({ active, onClick, children, icon: Icon, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-serif text-sm font-semibold transition-all border ${active ? "bg-cocoa text-cream border-cocoa shadow-sm" : "bg-cream text-cocoa border-line hover:border-amber-deep"}`}
    >
      {Icon && <Icon className="w-3.5 h-3.5" />}
      {children}
    </button>
  );
}

export default function Home() {
  const { logout, member, displayName } = useAuth();
  const nav = useNavigate();
  const [meta, setMeta] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [activeCat, setActiveCat] = useState(null);
  const [activeFest, setActiveFest] = useState(null);
  const [activeOrigen, setActiveOrigen] = useState(null);
  const [sinGluten, setSinGluten] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (activeCat) params.set("categoria", activeCat);
    if (activeFest) params.set("festividad", activeFest);
    if (activeOrigen) params.set("origen", activeOrigen);
    if (sinGluten) params.set("sin_gluten", "true");
    try {
      const [m, r] = await Promise.all([
        meta ? Promise.resolve({ data: meta }) : api().get("/meta"),
        api().get(`/recipes?${params.toString()}`),
      ]);
      if (!meta) setMeta(m.data);
      setRecipes(r.data.recipes);
    } catch (err) {
      if (err.response?.status === 401) { logout(); nav("/login"); }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [activeCat, activeFest, activeOrigen, sinGluten]);

  // Debounced live search: refetch 250ms after the user stops typing
  useEffect(() => {
    const t = setTimeout(() => { fetchData(); }, 250);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [q]);

  const onSearch = (e) => { e.preventDefault(); fetchData(); };

  // Semilla de shuffle: se genera al montar el home. Cada vez que alguien
  // vuelve al home ve un orden distinto de recetas (rotación de descubrimiento).
  // Si hay algún filtro activo (búsqueda, categoría, etc.) NO se mezcla —
  // así los resultados de búsqueda mantienen su orden natural.
  // Las 6 recetas MÁS NUEVAS quedan fijas arriba (con badge "Nueva") pa'
  // que Papi nunca pierda de vista lo recién agregado.
  const [shuffleSeed] = useState(() => Math.random());
  const NEW_ON_TOP = 6;
  const newestSlugs = useMemo(() => {
    // Usa el campo `order` — el Worker asigna el mayor `order` al recién publicado.
    const sorted = [...recipes].sort((a, b) => (b.order || 0) - (a.order || 0));
    return new Set(sorted.slice(0, NEW_ON_TOP).map((r) => r.slug));
  }, [recipes]);

  const shuffledRecipes = useMemo(() => {
    if (q || activeCat || activeFest || activeOrigen || sinGluten) return recipes;
    // Separa las nuevas (top) del resto
    const newest = [...recipes]
      .filter((r) => newestSlugs.has(r.slug))
      .sort((a, b) => (b.order || 0) - (a.order || 0));
    const rest = recipes.filter((r) => !newestSlugs.has(r.slug));
    // Fisher-Yates determinístico con la semilla — igual dentro de un login,
    // distinto entre logins.
    let s = shuffleSeed;
    for (let i = rest.length - 1; i > 0; i--) {
      s = (s * 9301 + 49297) % 233280;
      const j = Math.floor((s / 233280) * (i + 1));
      [rest[i], rest[j]] = [rest[j], rest[i]];
    }
    return [...newest, ...rest];
  }, [recipes, shuffleSeed, newestSlugs, q, activeCat, activeFest, activeOrigen, sinGluten]);

  const visibleRecipes = useMemo(() => showAll ? shuffledRecipes : shuffledRecipes.slice(0, 36), [shuffledRecipes, showAll]);
  const isNewSet = newestSlugs;

  const clearFilters = () => {
    setQ(""); setActiveCat(null); setActiveFest(null); setActiveOrigen(null); setSinGluten(false);
    setTimeout(fetchData, 0);
  };
  const hasFilter = !!(q || activeCat || activeFest || activeOrigen || sinGluten);

  return (
    <div className="min-h-screen paper-texture iphone-safe-top">
      <header className="sticky top-0 z-30 bg-paper/95 backdrop-blur-md border-b border-line">
        <div className="max-w-5xl mx-auto px-3 sm:px-5 py-3 flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="eyebrow text-[9px] sm:text-[10px] hidden sm:block">Familia Guendelman</p>
            <h1 className="font-display italic text-cocoa text-lg sm:text-2xl leading-tight truncate">El Recetario del Pollo</h1>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <Link data-testid="hub-link" to="/" className="p-2 sm:p-2.5 rounded-full bg-cream border border-line hover:border-amber-deep transition" aria-label="Inicio">
              <House className="w-4 h-4 sm:w-5 sm:h-5 text-cocoa" />
            </Link>
            <Link data-testid="add-recipe-link" to="/agregar" className="p-2 sm:p-2.5 rounded-full bg-cream border border-line hover:border-amber-deep transition" aria-label="Agregar receta">
              <Plus className="w-4 h-4 sm:w-5 sm:h-5 text-cocoa" />
            </Link>
            {member !== "invitado" && (
              <Link data-testid="gallery-link" to="/galeria" className="p-2 sm:p-2.5 rounded-full bg-cream border border-line hover:border-amber-deep transition" aria-label="Galería">
                <Image className="w-4 h-4 sm:w-5 sm:h-5 text-cocoa" />
              </Link>
            )}
            {member === "papa" && (
              <Link data-testid="admin-link" to="/admin" className="p-2 sm:p-2.5 rounded-full bg-cream border border-line hover:border-amber-deep transition" aria-label="Admin">
                <Settings className="w-4 h-4 sm:w-5 sm:h-5 text-cocoa" />
              </Link>
            )}
            <Link data-testid="weekly-menu-link" to="/menu-semanal" className="p-2 sm:p-2.5 rounded-full bg-cream border border-line hover:border-amber-deep transition" aria-label="Menú Semanal">
              <CalendarDays className="w-4 h-4 sm:w-5 sm:h-5 text-cocoa" />
            </Link>
            <Link data-testid="menus-link" to="/menus" className="p-2 sm:p-2.5 rounded-full bg-cream border border-line hover:border-amber-deep transition" aria-label="Mis menús">
              <BookHeart className="w-4 h-4 sm:w-5 sm:h-5 text-cocoa" />
            </Link>
            <Link data-testid="suggest-link" to="/sugerir" className="p-2 sm:p-2.5 rounded-full bg-cream border border-line hover:border-amber-deep transition" aria-label="Sugerir receta">
              <Send className="w-4 h-4 sm:w-5 sm:h-5 text-cocoa" />
            </Link>
            <button data-testid="logout-btn" onClick={() => { logout(); nav("/login"); }} className="p-2 sm:p-2.5 rounded-full bg-cream border border-line hover:border-amber-deep transition" aria-label="Salir">
              <LogOut className="w-4 h-4 sm:w-5 sm:h-5 text-cocoa" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 pb-20 pt-6">
        <BirthdayBanner member={member} displayName={displayName} />
        <PushNotificationsPrompt member={member} displayName={displayName} />
        <SundayMode recipes={recipes} />
        <FeaturesGuide />

        <div className="mb-6">
          <p className="font-serif italic text-cocoa-soft text-lg">
            {displayName ? `Hola, ${displayName}.` : "Hola, familia."} ¿Qué cocinamos hoy?
          </p>
        </div>

        <form onSubmit={onSearch} className="relative mb-4">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-cocoa-soft" />
          <input
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder='Busca: "pollo", "sin gluten", "judía", "peruana"…'
            className="w-full pl-12 pr-4 py-4 bg-cream border border-line rounded-full font-serif text-base text-cocoa focus:outline-none focus:ring-2 focus:ring-amber-deep/40 placeholder:text-cocoa-soft/60"
          />
        </form>

        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-3 mb-2">
          <Chip testId="filter-gf" active={sinGluten} onClick={() => setSinGluten((v) => !v)} icon={Wheat}>Sin gluten</Chip>
          <Chip testId="filter-judaica" active={activeCat === "Festividades Judías"} onClick={() => setActiveCat((v) => v === "Festividades Judías" ? null : "Festividades Judías")} icon={Star}>Festividades Judías</Chip>
          {meta?.origenes?.slice(0, 8).map((o) => (
            <Chip key={o} testId={`filter-origen-${o}`} active={activeOrigen === o} onClick={() => setActiveOrigen((v) => v === o ? null : o)}>{o}</Chip>
          ))}
        </div>

        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-4 mb-2">
          {meta?.categorias?.map((c) => (
            <Chip key={c.nombre} testId={`filter-cat-${c.nombre}`} active={activeCat === c.nombre} onClick={() => setActiveCat((v) => v === c.nombre ? null : c.nombre)}>
              {c.nombre} <span className="opacity-60">· {c.count}</span>
            </Chip>
          ))}
        </div>

        {hasFilter && (
          <button data-testid="clear-filters-btn" onClick={clearFilters} className="text-sm font-serif italic text-amber-deep underline mb-3">
            Limpiar filtros
          </button>
        )}

        <div className="flex items-baseline justify-between mb-3">
          <p className="eyebrow">{loading ? "Buscando…" : `${recipes.length} receta${recipes.length === 1 ? "" : "s"}`}</p>
          <button data-testid="surprise-btn" onClick={async () => {
            const r = await api().get("/recipes/random/1");
            if (r.data.recipes?.[0]) nav(`/receta/${r.data.recipes[0].slug}`);
          }} className="text-xs font-serif italic text-amber-deep flex items-center gap-1">
            <Shuffle className="w-3.5 h-3.5" /> Sorpréndeme
          </button>
        </div>

        <div data-testid="recipes-grid" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {visibleRecipes.map((r) => (
            <Link data-testid={`recipe-card-${r.slug}`} key={r.slug} to={`/receta/${r.slug}`} className="recipe-card flex flex-col relative">
              {isNewSet.has(r.slug) && !hasFilter && (
                <span className="absolute top-2 left-2 z-10 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-deep text-cream shadow-md">
                  NUEVA
                </span>
              )}
              <PhotoOrPlaceholder photo={r.photo} alt={r.nombre} />
              <div className="p-3 flex-1 flex flex-col">
                <p className="eyebrow text-[10px] mb-1.5 line-clamp-1">{r.categoria}</p>
                <h3 className="font-display text-cocoa text-lg leading-tight line-clamp-2 mb-2">{r.nombre}</h3>
                <div className="mt-auto flex items-center gap-2 flex-wrap text-[11px]">
                  {r.tiempo_preparacion && <span className="font-serif text-cocoa-soft">⏱ {r.tiempo_preparacion}</span>}
                  {r.sin_gluten && <span className="px-1.5 py-0.5 rounded bg-[var(--moss)]/20 text-[10px] font-bold" style={{color: 'var(--moss)'}}>SG</span>}
                  {r.source === "family" && <span className="px-1.5 py-0.5 rounded bg-amber-light/40 text-[10px] font-bold text-amber-deep">FAMILIA</span>}
                </div>
              </div>
            </Link>
          ))}
        </div>

        {recipes.length > visibleRecipes.length && (
          <div className="text-center mt-8">
            <button data-testid="show-all-btn" onClick={() => setShowAll(true)} className="btn-ghost">
              Ver las {recipes.length} recetas
            </button>
          </div>
        )}

        {!loading && recipes.length === 0 && (
          <div className="text-center py-20 font-serif italic text-cocoa-soft">
            Sin resultados. Prueba con otra palabra o limpia los filtros.
          </div>
        )}
      </main>
    </div>
  );
}
